import { Location } from '@cobliteam/common-types';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import useGeofenceRecommendations from '@cobliteam/hooks/src/useGeofenceRecommendations/useGeofenceRecommendations';
import useHasFewGeofences from '@cobliteam/hooks/src/useGeofenceRecommendations/useHasFewGeofences';
import { useNudge } from '@cobliteam/hooks/src/useNudge';
import type { Nudge, NudgeSurface } from '@cobliteam/hooks/src/useNudge/types';
import { useTranslation } from '@cobliteam/i18n';
import NudgeMessage from '@cobliteam/ui-business/components/Nudge/NudgeMessage';
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export const GEOFENCE_NUDGE_ID = 'geofence-recommendations';

/**
 * Recommendation: the places come from an AI workflow over the fleet's most-visited locations, so
 * this hits a back end.
 *
 * Visibility uses the CHEAP gate only — the `poisRecommendations` flag plus the under-2-places
 * check. The expensive workflow is deferred until the nudge is expanded, because the stack lives
 * in the app shell: running it on mount would fire the workflow on every page of the dashboard.
 */
export const useGeofenceNudge = (surface: NudgeSurface = 'floating-card'): Nudge | null => {
  const { t } = useTranslation('geofences');
  const { t: tHeader } = useTranslation('app-header');
  const history = useHistory();
  const { pathname } = useLocation();
  const { getSession } = useUser();
  const fleetId = getSession()?.fleet?.id;
  const { poisRecommendations } = useFeatureFlags();

  const isOnOwnPage = pathname.startsWith(Location.GEOFENCES);

  // Set by the stack through `onExpand`; until then the workflow query stays disabled.
  const [expanded, setExpanded] = useState(false);

  const { isEligible } = useHasFewGeofences(fleetId, poisRecommendations);

  const { shouldShow, dismiss, accept } = useNudge({
    id: GEOFENCE_NUDGE_ID,
    kind: 'recommendation',
    surface,
    // Suppressed on the domain's own page, where the in-page banner already says this — the two
    // surfaces are complementary, not simultaneous. It also keeps the nudge's collapsed row from
    // duplicating the banner's copy, which is what the e2e suite flagged.
    useShouldShow: () => poisRecommendations && isEligible && !isOnOwnPage,
  });

  const { data, isFetching } = useGeofenceRecommendations({ enabled: shouldShow && expanded });
  const count = data?.length ?? 0;
  const first = data?.[0];

  if (!shouldShow) return null;

  const hasResults = count > 0 && !!first;
  // Collapsed (and while generating) the title is an invitation, not a claim — the workflow has
  // not run yet, so promising N places here would be a promise we cannot keep.
  const title = hasResults ? t('recommendations.banner_title', { count }) : t('recommendations.nudge_title');

  const describe = () => {
    if (hasResults) return t('recommendations.banner_description');
    if (isFetching) return t('recommendations.loading_recommendation');
    return t('recommendations.empty_state_description');
  };

  return {
    id: GEOFENCE_NUDGE_ID,
    kind: 'recommendation',
    priority: 0,
    tagLabel: tHeader('aiRecommendations.recommendationTag'),
    title,
    dismiss,
    onExpand: () => setExpanded(true),
    render: () => (
      <NudgeMessage
        title={title}
        description={describe()}
        ctaLabel={hasResults ? t('recommendations.banner_cta') : undefined}
        onCta={
          hasResults
            ? () => {
                accept({ count });
                // Deep-links into the create form prefilled with the first recommendation, the
                // same target `useAiRecommendationCta` used for this journey.
                history.push(`${Location.GEOFENCES_CREATE}?recommendationId=${first.id}`);
              }
            : undefined
        }
        dismissLabel={tHeader('aiRecommendations.dismiss')}
        onDismiss={() => dismiss('not-interested')}
      />
    ),
  };
};

export default useGeofenceNudge;
